import React, { ReactNode, useMemo, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { FormGroup } from './FormGroup';
import InputError from './InputError';
import InputLabel from './InputLabel';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type SwitchProps = {
  label?: string;
  name: string;
  id?: string;
  checked?: boolean;
  placeholder?: string;
  children?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  className?: string;
  hint?: ReactNode;
  onChange?: (value: boolean) => void;
};

type ControlledSwitchProps = Omit<SwitchProps, 'label' | 'required'>;

export const ControlledSwitchInput: React.FC<
  React.PropsWithChildren<ControlledSwitchProps>
> = ({ name, disabled, className, onChange, checked, ...rest }) => {
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();
  const error = getError(name, errors);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Form.Check
          {...rest}
          ref={field.ref}
          name={field.name}
          onBlur={field.onBlur}
          className={className}
          type="switch"
          disabled={disabled}
          isInvalid={!!error}
          readOnly={isSubmitting}
          checked={checked ?? !!field.value}
          onChange={(event) => {
            field.onChange(event.target.checked);
            onChange?.(event.target.checked);
          }}
        />
      )}
    />
  );
};

export const SwitchInput: React.FC<React.PropsWithChildren<SwitchProps>> = ({
  onChange,
  ...rest
}) => (
  <Form.Check
    type="switch"
    onChange={(event) => onChange?.(event.target.checked)}
    {...rest}
  />
);

export const SwitchInputFormGroup: React.FC<
  React.PropsWithChildren<SwitchProps>
> = ({ id, label, name, required, hint, ...rest }) => {
  const ref = useRef(null);
  const {
    formState: { errors },
  } = useFormContext();
  const error = useMemo(() => getError(name, errors), [name, errors]);

  return React.useMemo(
    () => (
      <FormGroup ref={ref}>
        <Row>
          <Col>
            <InputLabel
              inputId={id}
              label={label}
              required={required}
              hint={hint}
              className="form-check-label"
            />
          </Col>
          <Col className="col-auto">
            <ControlledSwitchInput id={id} name={name} {...rest} />
          </Col>
        </Row>
        {error && (
          <Row>
            <Col>
              <InputError error={error} className="d-block" />
            </Col>
          </Row>
        )}
        <ScrollToError name={name} inputContainerRef={ref} />
      </FormGroup>
    ),
    [error, hint, id, label, name, ref, required, rest],
  );
};
